import React, { useState, useEffect } from 'react';
import { X, Search, AlertTriangle } from 'lucide-react';
import { API_URL } from '../config/api';
import './AppointmentForm.css';

const AppointmentForm = ({ onSuccess, onCancel, patientId, patientName }) => {
    const [patientSearch, setPatientSearch] = useState('');
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(
        patientId ? { id: patientId, name: patientName } : null
    );
    const [doctors, setDoctors] = useState([]); // Added missing state
    const [patients, setPatients] = useState([]); // Added missing state

    const [formData, setFormData] = useState({
        patient_id: patientId || '',
        branch: 'GENETICA',
        doctor_id: '',
        doctor_name: '',
        status: 'pending',
        appointment_date: '',
        start_time: '',
        end_time: '',
        office: '',
        custom_office: '',
        reason: '',
        notes: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [conflicts, setConflicts] = useState({ doctor: false, office: false });
    const [conflictMessages, setConflictMessages] = useState([]);
    const [checkingConflicts, setCheckingConflicts] = useState(false);



    useEffect(() => {
        fetchDoctors();
        if (!patientId) {
            fetchPatients();
        }
    }, []);

    // Check conflicts in real-time when relevant fields change
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (formData.doctor_id && formData.appointment_date && formData.start_time && formData.end_time) {
                checkAvailability();
            } else {
                setConflicts({ doctor: false, office: false });
                setConflictMessages([]);
            }
        }, 500); // Debounce 500ms

        return () => clearTimeout(timeoutId);
    }, [formData.doctor_id, formData.appointment_date, formData.start_time, formData.end_time, formData.office, formData.branch]);

    const checkAvailability = async () => {
        setCheckingConflicts(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                doctor_id: formData.doctor_id,
                date: formData.appointment_date,
                start_time: formData.start_time,
                end_time: formData.end_time,
                office: formData.office === 'Otro' ? formData.custom_office : formData.office,
                branch: formData.branch
            });

            const response = await fetch(`${API_URL}/appointments/check-availability?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setConflicts(data.conflicts || { doctor: false, office: false });

                const messages = [];
                if (data.conflicts?.doctor) {
                    messages.push('Este médico ya tiene una cita en este horario.');
                }
                if (data.conflicts?.office) {
                    messages.push('El consultorio está ocupado en este horario.');
                }
                setConflictMessages(messages);
            } else {
                // If endpoint doesn't exist, don't show errors
                setConflicts({ doctor: false, office: false });
                setConflictMessages([]);
            }
        } catch (err) {
            console.error('Error checking availability:', err);
        } finally {
            setCheckingConflicts(false);
        }
    };

    const fetchDoctors = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/doctors?linked=1`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setDoctors(data);
            }
        } catch (err) {
            console.error('Error fetching doctors:', err);
        }
    };

    const fetchPatients = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const result = await response.json();
                // Handle both { data: [...] } and direct array responses
                const patientList = Array.isArray(result) ? result : (result.data || []);
                setPatients(patientList);
            }
        } catch (err) {
            console.error('Error fetching patients:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'doctor_id') {
            const selectedDoctor = doctors.find(d => d.id == value);
            if (selectedDoctor) {
                setFormData(prev => ({
                    ...prev,
                    doctor_name: selectedDoctor.name
                }));
            }
        }

        // Clear error when user makes changes
        setError('');
    };

    const handlePatientSearch = (e) => {
        const value = e.target.value;
        setPatientSearch(value);
        setShowPatientDropdown(true);
    };

    const filteredPatients = patients.filter(p => {
        const fullName = `${p.first_name} ${p.last_name}${p.maternal_last_name ? ' ' + p.maternal_last_name : ''}`.toLowerCase();
        const searchLower = patientSearch.toLowerCase();
        return fullName.includes(searchLower) ||
            (p.phone && p.phone.includes(patientSearch)) ||
            (p.patient_code && p.patient_code.toLowerCase().includes(searchLower));
    }).slice(0, 10);

    const selectPatient = (patient) => {
        setSelectedPatient({
            id: patient.id,
            name: `${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`,
            patient_code: patient.patient_code || null
        });
        setFormData(prev => ({
            ...prev,
            patient_id: patient.id
        }));
        setPatientSearch('');
        setShowPatientDropdown(false);
    };

    const hasConflicts = conflicts.doctor || conflicts.office;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) {
            return;
        }
        setLoading(true);
        setError('');

        if (!formData.patient_id) {
            setError('Debe seleccionar un paciente');
            setLoading(false);
            return;
        }

        // Prevent submission if there are conflicts
        if (hasConflicts) {
            setError('No se puede guardar la cita debido a conflictos de horario. Por favor corrija los errores.');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');

            if (!token) {
                setError('No hay sesión activa. Por favor inicie sesión nuevamente.');
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_URL}/appointments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                onSuccess();
            } else {
                const data = await response.json();
                // Handle conflict errors from backend
                if (data.errors && Array.isArray(data.errors)) {
                    setError(data.errors.join(' '));
                    if (data.conflicts) {
                        setConflicts(data.conflicts);
                    }
                } else {
                    setError(data.message || 'Error al agendar cita');
                }
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content appointment-modal">
                <div className="modal-header">
                    <h2>Nueva Cita</h2>
                    <button onClick={onCancel} className="close-btn">
                        <X size={24} />
                    </button>
                </div>

                {error && <div className="error-message">{error}</div>}

                {/* Conflict Warnings */}
                {conflictMessages.length > 0 && (
                    <div className="conflict-warnings">
                        <AlertTriangle size={20} />
                        <div>
                            {conflictMessages.map((msg, i) => (
                                <p key={i}>{msg}</p>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="appointment-form">
                    {/* Patient Selection */}
                    <div className="form-group">
                        <label>Paciente *</label>
                        {patientId ? (
                            <input
                                type="text"
                                value={patientName || 'Cargando...'}
                                readOnly
                                className="readonly-input"
                            />
                        ) : (
                            <div className="patient-search-container">
                                {selectedPatient ? (
                                    <div className="selected-patient">
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {selectedPatient.patient_code && (
                                                <span style={{
                                                    background: '#0a5483',
                                                    color: '#fff',
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'bold'
                                                }}>{selectedPatient.patient_code}</span>
                                            )}
                                            {selectedPatient.name}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedPatient(null);
                                                setFormData(prev => ({ ...prev, patient_id: '' }));
                                            }}
                                            className="btn-clear-patient"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="search-input-wrapper">
                                            <input
                                                type="text"
                                                value={patientSearch}
                                                onChange={handlePatientSearch}
                                                onFocus={() => setShowPatientDropdown(true)}
                                                placeholder="Buscar paciente por nombre o teléfono..."
                                            />
                                        </div>
                                        {showPatientDropdown && patientSearch && (
                                            <div className="patient-dropdown">
                                                {filteredPatients.length > 0 ? (
                                                    filteredPatients.map(p => (
                                                        <div
                                                            key={p.id}
                                                            className="patient-option"
                                                            onClick={() => selectPatient(p)}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                                                {p.patient_code && (
                                                                    <span style={{
                                                                        background: '#0a5483',
                                                                        color: '#fff',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '4px',
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 'bold',
                                                                        whiteSpace: 'nowrap',
                                                                        flexShrink: 0
                                                                    }}>{p.patient_code}</span>
                                                                )}
                                                                <strong style={{ flex: 1 }}>{p.first_name} {p.last_name} {p.maternal_last_name || ''}</strong>
                                                                {p.phone && <span className="patient-phone">{p.phone}</span>}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="no-results">No se encontraron pacientes</div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Branch */}
                    <div className="form-group">
                        <label>Sucursal</label>
                        <select
                            name="branch"
                            value={formData.branch}
                            onChange={handleChange}
                            required
                        >
                            <option value="GENETICA">GENETICA</option>
                            <option value="MATRIZ">MATRIZ</option>
                            <option value="NORTE">NORTE</option>
                        </select>
                    </div>

                    {/* Doctor */}
                    <div className={`form-group ${conflicts.doctor ? 'has-conflict' : ''}`}>
                        <label>Médico *</label>
                        <select
                            name="doctor_id"
                            value={formData.doctor_id}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Seleccione un médico</option>
                            {doctors.map(doctor => (
                                <option key={doctor.id} value={doctor.id}>
                                    {doctor.name}
                                </option>
                            ))}
                        </select>
                        {conflicts.doctor && (
                            <span className="field-conflict-hint">Este médico ya tiene una cita en este horario</span>
                        )}
                    </div>

                    {/* Status Info */}
                    <div className="form-group">
                        <label>Estatus</label>
                        <input
                            type="text"
                            value="Por confirmar"
                            readOnly
                            className="readonly-input status-pending"
                        />
                        <small className="form-hint">El estatus se puede cambiar después de crear la cita</small>
                    </div>

                    {/* Date */}
                    <div className="form-group">
                        <label>Fecha *</label>
                        <input
                            type="date"
                            name="appointment_date"
                            value={formData.appointment_date}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* Start and End Time */}
                    <div className="form-row">
                        <div className={`form-group ${hasConflicts ? 'has-conflict' : ''}`}>
                            <label>Hora Inicio *</label>
                            <input
                                type="time"
                                name="start_time"
                                value={formData.start_time}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className={`form-group ${hasConflicts ? 'has-conflict' : ''}`}>
                            <label>Hora Fin *</label>
                            <input
                                type="time"
                                name="end_time"
                                value={formData.end_time}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>
                    {checkingConflicts && (
                        <div className="checking-availability">Verificando disponibilidad...</div>
                    )}

                    {/* Office */}
                    <div className={`form-group ${conflicts.office ? 'has-conflict' : ''}`}>
                        <label>Consultorio *</label>
                        <select
                            name="office"
                            value={formData.office}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Seleccione un consultorio</option>
                            <option value="Consultorio 1">Consultorio 1</option>
                            <option value="Consultorio 2">Consultorio 2</option>
                            <option value="Consultorio 3">Consultorio 3</option>
                            <option value="Consultorio 4">Consultorio 4</option>
                            <option value="Otro">Otro</option>
                        </select>
                        {conflicts.office && (
                            <span className="field-conflict-hint">El consultorio está ocupado en este horario</span>
                        )}
                    </div>

                    {formData.office === 'Otro' && (
                        <div className="form-group">
                            <label>Especificar Consultorio</label>
                            <input
                                type="text"
                                name="custom_office"
                                value={formData.custom_office}
                                onChange={handleChange}
                                placeholder="Ingrese el nombre del consultorio"
                                required
                            />
                        </div>
                    )}

                    {/* Reason */}
                    <div className="form-group">
                        <label>Motivo de Consulta *</label>
                        <input
                            type="text"
                            name="reason"
                            value={formData.reason}
                            onChange={handleChange}
                            placeholder="Ej: Consulta general, Seguimiento..."
                            required
                        />
                    </div>

                    {/* Notes */}
                    <div className="form-group">
                        <label>Notas Adicionales</label>
                        <textarea
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            rows="3"
                            placeholder="Notas o instrucciones especiales..."
                        />
                    </div>

                    <div className="form-actions">
                        <button type="button" onClick={onCancel} className="btn-secondary">
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={loading || hasConflicts}
                        >
                            {loading ? 'Guardando...' : hasConflicts ? 'Corregir Conflictos' : 'Agendar Cita'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AppointmentForm;
